import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { AddCustomRoleModal } from "./AddCustomRoleModal";
import {
  CreateCustomRolePayload,
  CustomRoleListItem,
  CustomRoleScope,
  useCreateCustomRole,
  useCustomRoles,
  useDeleteCustomRole,
  useRolePermissionCatalog,
  useUpdateCustomRole,
} from "@/components/pages/users/hooks/useCustomRoles";
import { formatCustomRoleError } from "@/components/pages/users/utils/customRoleErrors";
import { useGuesteriaAdmins } from "@/components/pages/users/hooks/useGuesteriaAdmins";
import { cn } from "@/lib/utils";
import { Role } from "@/types/user";
import { PLATFORM_ROLE_PERMISSION_RESOURCES } from "@/components/pages/users/utils/platformRolePermissions";
import { PlatformRolePermissionCells } from "./PlatformRolePermissionCells";

type RolesTabProps = {
  backTo: string | null;
  canAddCustomRole: boolean;
  scope: CustomRoleScope;
  allowedBaseRoles?: Role[];
};

export const RolesTab: React.FC<RolesTabProps> = ({
  backTo,
  canAddCustomRole,
  scope,
  allowedBaseRoles,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = React.useState(false);
  const [editingRole, setEditingRole] =
    React.useState<CustomRoleListItem | null>(null);
  const [roleToDelete, setRoleToDelete] =
    React.useState<CustomRoleListItem | null>(null);
  const { data: customRoles = [] } = useCustomRoles(scope);
  const {
    data: permissionCatalog = [],
    isLoading: isPermissionCatalogLoading,
  } = useRolePermissionCatalog(scope, isAddRoleModalOpen);
  const createCustomRoleMutation = useCreateCustomRole(scope);
  const updateCustomRoleMutation = useUpdateCustomRole(scope);
  const deleteCustomRoleMutation = useDeleteCustomRole(scope);
  const isPlatformScope = scope === "platform";
  const systemRoleTableGridClassName = "grid-cols-[2fr_1fr_1fr_120px]";
  const customRoleTableGridClassName = isPlatformScope
    ? "grid-cols-[minmax(220px,2fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(120px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_96px]"
    : "grid-cols-[2fr_1fr_1fr_1fr_120px]";

  const customRoleById = React.useMemo(
    () => new Map(customRoles.map((customRole) => [customRole.id, customRole])),
    [customRoles],
  );

  const { data: guesteriaAdminsResponse } = useGuesteriaAdmins(
    { limit: 1 },
    isPlatformScope,
  );
  const guesteriaAdminCount = guesteriaAdminsResponse?.total ?? 0;

  const platformSystemRoles = React.useMemo<
    {
      key: string;
      name: string;
      description: string;
      assigneeCount: number | null;
    }[]
  >(
    () =>
      isPlatformScope
        ? [
            {
              key: "SUPER_ADMIN",
              name: t("users.table.superAdmin", "Super Admin"),
              description: t(
                "users.roles.systemRoles.superAdminDescription",
                "Full platform administration across all tenants.",
              ),
              assigneeCount: null,
            },
            {
              key: "GUESTERIA_ADMIN",
              name: t("users.table.guesteriaAdmin", "Guesteria Admin"),
              description: t(
                "users.roles.systemRoles.guesteriaAdminDescription",
                "Platform operations and tenant support.",
              ),
              assigneeCount: guesteriaAdminCount,
            },
          ]
        : [],
    [isPlatformScope, t, guesteriaAdminCount],
  );

  const handleCreateCustomRole = React.useCallback(
    async (payload: CreateCustomRolePayload) => {
      try {
        await createCustomRoleMutation.mutateAsync(payload);
        const translationKey = isPlatformScope
          ? "users.roles.actions.platformCreated"
          : "users.roles.actions.created";
        const defaultValue = isPlatformScope
          ? "Platform role '{{name}}' created"
          : "Custom role '{{name}}' created";
        toast.success(
          t(translationKey, {
            defaultValue,
            name: payload.name,
          }),
        );
        setIsAddRoleModalOpen(false);
      } catch (error) {
        toast.error(formatCustomRoleError(error));
        throw error;
      }
    },
    [createCustomRoleMutation, isPlatformScope, t],
  );

  const handleUpdateCustomRole = React.useCallback(
    async (payload: CreateCustomRolePayload) => {
      if (!editingRole) {
        return;
      }

      try {
        await updateCustomRoleMutation.mutateAsync({
          id: editingRole.id,
          payload: {
            name: payload.name,
            description: payload.description ?? null,
            permissionKeys: payload.permissionKeys,
          },
        });
        let banner = t("users.roles.actions.updated", {
          defaultValue: "Custom role '{{name}}' updated.",
          name: payload.name,
        });

        if (isPlatformScope) {
          banner = t("users.roles.actions.platformUpdated", {
            defaultValue:
              "Platform role '{{name}}' updated. Changes apply on affected users' next request.",
            name: payload.name,
          });
        } else if (editingRole.assigneeCount > 0) {
          banner = t("users.roles.actions.updatedWithImpact", {
            defaultValue:
              "Custom role '{{name}}' updated. Permissions for {{count}} user(s) will refresh on next sign-in.",
            name: payload.name,
            count: editingRole.assigneeCount,
          });
        }

        toast.success(banner);
        setEditingRole(null);
        setIsAddRoleModalOpen(false);
      } catch (error) {
        toast.error(formatCustomRoleError(error));
        throw error;
      }
    },
    [editingRole, isPlatformScope, updateCustomRoleMutation, t],
  );

  const handleEditRole = React.useCallback(
    (roleId: string) => {
      if (!roleId) {
        return;
      }
      const roleToEdit = customRoleById.get(roleId);
      if (!roleToEdit) {
        return;
      }
      setEditingRole(roleToEdit);
      setIsAddRoleModalOpen(true);
    },
    [customRoleById],
  );

  const getAssignedRoleDeleteMessage = (role: CustomRoleListItem): string =>
    t("users.roles.actions.deleteBlockedAssigned", {
      defaultValue:
        "This role is assigned to {{count}} user(s). Reassign them before deleting the role.",
      count: role.assigneeCount,
    });

  const getDeleteRoleTitle = (role: CustomRoleListItem): string | undefined => {
    if (!canAddCustomRole) {
      return t("common.insufficientPermissions", "Insufficient permissions");
    }
    if (!isPlatformScope && role.assigneeCount > 0) {
      return getAssignedRoleDeleteMessage(role);
    }
    return undefined;
  };

  const handleDeleteRole = (role: CustomRoleListItem): void => {
    if (!isPlatformScope && role.assigneeCount > 0) {
      toast.error(getAssignedRoleDeleteMessage(role));
      return;
    }

    setRoleToDelete(role);
  };

  const handleConfirmDelete = React.useCallback(async () => {
    if (!roleToDelete) return;
    try {
      await deleteCustomRoleMutation.mutateAsync(roleToDelete.id);
      let banner = t("users.roles.actions.deleted", {
        defaultValue: "Custom role '{{name}}' deleted.",
        name: roleToDelete.name,
      });

      if (isPlatformScope) {
        banner = t("users.roles.actions.platformDeleted", {
          defaultValue:
            "Platform role '{{name}}' deleted. Affected users lose permissions on their next request.",
          name: roleToDelete.name,
        });
      }

      toast.success(banner);
      setRoleToDelete(null);
    } catch (error) {
      toast.error(formatCustomRoleError(error));
    }
  }, [deleteCustomRoleMutation, isPlatformScope, roleToDelete, t]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {backTo ? (
            <Button variant="outline" onClick={() => navigate(backTo)}>
              {t("users.page.back")}
            </Button>
          ) : null}
          <h2 className="text-lg leading-none font-medium text-[#0A0A0A]">
            {isPlatformScope
              ? t("users.roles.platformRoleTypes", "Platform roles")
              : t("users.roles.roleTypes")}
          </h2>
        </div>

        <Button
          variant="new_default"
          onClick={() => {
            setEditingRole(null);
            setIsAddRoleModalOpen(true);
          }}
          disabled={!canAddCustomRole}
          title={
            !canAddCustomRole
              ? t("common.insufficientPermissions", "Insufficient permissions")
              : undefined
          }
        >
          <Plus className="h-4 w-4" />
          {isPlatformScope
            ? t("users.roles.addPlatformRole", "Create Platform Role")
            : t("users.roles.addCustomRole")}
        </Button>
      </div>

      {isPlatformScope ? (
        <div className="pt-4">
          <div className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white">
            <div
              className={cn(
                "grid border-b border-[#E5E7EB] bg-white px-3 py-2 text-sm font-medium",
                systemRoleTableGridClassName,
              )}
            >
              <div>{t("users.roles.columns.role", "Role")}</div>
              <div aria-hidden="true" />
              <div>{t("users.roles.assignees", "Assignees")}</div>
              <div aria-hidden="true" />
            </div>
            {platformSystemRoles.map((role) => (
              <div
                key={role.key}
                className={cn(
                  "grid items-center border-b border-[#E5E7EB] px-3 py-3 last:border-b-0",
                  systemRoleTableGridClassName,
                )}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{role.name}</p>
                  <p className="truncate text-xs text-[#6B7280]">
                    {role.description}
                  </p>
                </div>
                <div aria-hidden="true" />
                <div className="text-xs">{role.assigneeCount ?? "—"}</div>
                <div aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="pt-4">
        <h3 className="pb-4 text-lg leading-none font-medium text-[#0A0A0A]">
          {isPlatformScope
            ? t("users.roles.platformCustom", "Platform custom roles")
            : t("users.roles.custom")}
        </h3>
        <div className="overflow-x-auto rounded-2xl border border-[#E5E7EB] bg-[#F3F4F6]">
          <div
            className={cn(
              "grid border-b border-[#E5E7EB] bg-white px-3 py-2 text-sm font-medium",
              customRoleTableGridClassName,
              isPlatformScope && "min-w-[1140px]",
            )}
          >
            <div>{t("users.roles.columns.role", "Role")}</div>
            {isPlatformScope ? (
              PLATFORM_ROLE_PERMISSION_RESOURCES.map((resource) => (
                <div key={resource}>{t(`users.roles.columns.${resource}`)}</div>
              ))
            ) : (
              <>
                <div>{t("users.roles.baseRole", "Base role")}</div>
                <div>{t("users.roles.permissions", "Permissions")}</div>
                <div>{t("users.roles.assignees", "Assignees")}</div>
              </>
            )}
            <div>{t("users.table.actions")}</div>
          </div>
          {customRoles.map((role) => (
            <div
              key={role.id}
              className={cn(
                "grid items-center border-b border-[#E5E7EB] px-3 py-3 last:border-b-0",
                customRoleTableGridClassName,
                isPlatformScope && "min-w-[1140px]",
              )}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{role.name}</p>
                {role.description ? (
                  <p className="truncate text-xs text-[#6B7280]">
                    {role.description}
                  </p>
                ) : null}
              </div>
              {isPlatformScope ? (
                <PlatformRolePermissionCells
                  permissionKeys={role.permissionKeys}
                />
              ) : (
                <div className="text-xs">
                  {"baseRole" in role ? role.baseRole : "—"}
                </div>
              )}
              {!isPlatformScope ? (
                <>
                  <div className="text-xs">{role.permissionKeys.length}</div>
                  <div className="text-xs">{role.assigneeCount}</div>
                </>
              ) : null}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="new_ghost"
                  size="icon_sm"
                  onClick={() => handleEditRole(role.id)}
                  disabled={!canAddCustomRole}
                  aria-label={t("users.roles.actions.editRole", {
                    defaultValue: "Edit {{name}}",
                    name: role.name,
                  })}
                  title={
                    !canAddCustomRole
                      ? t(
                          "common.insufficientPermissions",
                          "Insufficient permissions",
                        )
                      : undefined
                  }
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="new_ghost"
                  size="icon_sm"
                  onClick={() => handleDeleteRole(role)}
                  disabled={
                    !canAddCustomRole || deleteCustomRoleMutation.isPending
                  }
                  aria-label={t("users.roles.actions.deleteRole", {
                    defaultValue: "Delete {{name}}",
                    name: role.name,
                  })}
                  title={getDeleteRoleTitle(role)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AddCustomRoleModal
        open={isAddRoleModalOpen}
        scope={scope}
        permissionCatalog={permissionCatalog}
        isPermissionCatalogLoading={isPermissionCatalogLoading}
        onClose={() => {
          setIsAddRoleModalOpen(false);
          setEditingRole(null);
        }}
        mode={editingRole ? "edit" : "create"}
        initialRole={editingRole || undefined}
        assigneeCount={editingRole?.assigneeCount ?? 0}
        onSubmit={editingRole ? handleUpdateCustomRole : handleCreateCustomRole}
        isSubmitting={
          createCustomRoleMutation.isPending ||
          updateCustomRoleMutation.isPending
        }
        allowedBaseRoles={allowedBaseRoles}
      />

      <Dialog
        open={Boolean(roleToDelete)}
        onOpenChange={(open) => !open && setRoleToDelete(null)}
      >
        <DialogContent className="w-[460px] max-w-[460px] gap-4 rounded-md border border-[#E5E7EB] bg-white p-6">
          <DialogHeader>
            <DialogTitle>
              {isPlatformScope
                ? t("users.roles.actions.confirmDeletePlatformTitle", {
                    defaultValue: "Delete platform role '{{name}}'?",
                    name: roleToDelete?.name ?? "",
                  })
                : t("users.roles.actions.confirmDeleteTitle", {
                    defaultValue: "Delete custom role '{{name}}'?",
                    name: roleToDelete?.name ?? "",
                  })}
            </DialogTitle>
            <DialogDescription>
              {isPlatformScope
                ? t("users.roles.actions.confirmDeletePlatformBody", {
                    defaultValue:
                      "This role is currently assigned to {{count}} platform admin(s). Their permissions update on their next request. This cannot be undone.",
                    count: roleToDelete?.assigneeCount ?? 0,
                  })
                : t("users.roles.actions.confirmDeleteBody", {
                    defaultValue:
                      "Only roles without assigned users can be deleted. This cannot be undone.",
                  })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRoleToDelete(null)}
              disabled={deleteCustomRoleMutation.isPending}
            >
              {t("users.roles.modal.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleConfirmDelete()}
              disabled={deleteCustomRoleMutation.isPending}
            >
              {deleteCustomRoleMutation.isPending
                ? t("users.form.updating")
                : t("users.roles.actions.confirmDelete", "Delete role")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
